import React, { useState } from 'react';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from 'components/ui/dropdown-menu';
import { Bell } from 'lucide-react';
import { navMutedClass } from '../navIcons';
import { cn } from 'utils/cn';
import { MOCK_NOTIFICATIONS } from '../mockData';

const NotificationThumb = ({ item }) => {
    const src = item.image || item.avatar;
    const label = item.name || item.title;
    const initials = item.initials || label.slice(0, 2).toUpperCase();
    const isCover = item.kind === 'event' || item.kind === 'job';
    const shape = isCover ? 'rounded-md' : 'rounded-full';

    if (!src) {
        return (
            <span className={cn('h-9 w-9 bg-muted flex items-center justify-center text-[11px] font-semibold', shape)}>
                {initials}
            </span>
        );
    }

    return (
        <img src={src} alt={label} className={cn('h-9 w-9 object-cover bg-muted', shape)} />
    );
};

const NotificationDot = () => (
    <span className="absolute top-1.5 right-1.5 flex h-2 w-2">
        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75" />
        <span className="relative inline-flex rounded-full h-2 w-2 bg-rose-500" />
    </span>
);

export const NavNotifications = ({
    notifications = MOCK_NOTIFICATIONS,
    unreadCount,
    onNavigate,
    onOpen,
    onDismiss,
    onClearAll,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    clearAllLabel = 'Clear all',
    className,
}) => {
    const [open, setOpen] = useState(false);
    const [hiddenIds, setHiddenIds] = useState([]);
    const visible = notifications.filter((item) => !hiddenIds.includes(item.id));
    const unread = unreadCount ?? visible.filter((item) => item.unread).length;

    const handleOpenChange = (nextOpen) => {
        setOpen(nextOpen);
        if (nextOpen && onOpen) {
            onOpen();
        }
    };

    const hideLocally = (ids) => {
        setHiddenIds((prev) => [...prev, ...ids.filter((id) => !prev.includes(id))]);
    };

    const handleItemClick = (item) => {
        if (onDismiss) {
            onDismiss(item);
        } else {
            hideLocally([item.id]);
        }
        setOpen(false);
        if (onNavigate) onNavigate(item.to || '/notifications');
    };

    const handleClearAll = (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (onClearAll) {
            onClearAll();
        } else {
            hideLocally(visible.map((item) => item.id));
        }
    };

    const handleScroll = (event) => {
        if (!onLoadMore || !hasMore || loadingMore) {
            return;
        }
        const node = event.currentTarget;
        if (node.scrollTop + node.clientHeight >= node.scrollHeight - 32) {
            onLoadMore();
        }
    };

    return (
        <DropdownMenu open={open} onOpenChange={handleOpenChange}>
            <DropdownMenuTrigger asChild>
                <button
                    type="button"
                    className={cn(
                        'relative p-2 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        navMutedClass,
                        'hover:text-[var(--primary-text-color)] hover:bg-[color:var(--dark-secondary-color)]/15',
                        open && 'bg-[color:var(--dark-secondary-color)]/15 text-[var(--primary-text-color)]',
                        className
                    )}
                    aria-label="Notifications"
                >
                    <Bell className="w-4 h-4" />
                    {unread > 0 ? <NotificationDot /> : null}
                </button>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end" className="w-80 p-1.5 shadow-xl">
                <div className="flex items-center justify-between px-2.5 py-2">
                    <p className="text-sm font-semibold text-foreground">Notifications</p>
                    {unread > 0 ? (
                        <span className="text-[11px] font-semibold text-primary">{unread} new</span>
                    ) : null}
                </div>

                {visible.length === 0 ? (
                    <p className="px-2.5 py-6 text-center text-xs text-muted-foreground">
                        Nothing new for now
                    </p>
                ) : (
                    <div
                        data-testid="notification-list"
                        className="max-h-80 overflow-y-auto"
                        onScroll={handleScroll}
                    >
                        {visible.map((item) => (
                            <DropdownMenuItem
                                key={item.id}
                                onClick={() => handleItemClick(item)}
                                className="flex items-start gap-2.5 px-2.5 py-2 rounded-md cursor-pointer overflow-visible"
                            >
                                <span className="relative shrink-0 mt-0.5 overflow-visible">
                                    <NotificationThumb item={item} />
                                    {item.unread ? (
                                        <span
                                            data-unread-thumb
                                            className="absolute top-0.5 right-0.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-popover"
                                        />
                                    ) : null}
                                </span>
                                <span className="min-w-0 flex-1">
                                    <span className={cn(
                                        'block text-sm leading-snug break-words line-clamp-3',
                                        item.unread ? 'font-semibold text-foreground' : 'text-muted-foreground'
                                    )}>
                                        {item.title}
                                    </span>
                                    <span className="block text-[11px] text-muted-foreground mt-0.5">
                                        {item.time}
                                    </span>
                                </span>
                            </DropdownMenuItem>
                        ))}
                        {loadingMore ? (
                            <p className="px-2.5 py-2 text-center text-[11px] text-muted-foreground">Loading...</p>
                        ) : null}
                    </div>
                )}
                {visible.length > 0 ? (
                    <button
                        type="button"
                        onClick={handleClearAll}
                        className="mt-1 w-full rounded-md px-2.5 py-2 text-xs font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
                    >
                        {clearAllLabel}
                    </button>
                ) : null}
            </DropdownMenuContent>
        </DropdownMenu>
    );
};
